import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, LogIn, ArrowLeft, Sparkles, CheckCircle, Shield, Users, Zap } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Navigate to dashboard only when user is actually authenticated
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent any default form submission behavior that might cause refresh
    e.stopPropagation();
    
    setLoading(true);

    try {
      const result = await login(formData, rememberMe);
      
      if (result.success) {
        toast({
          title: "Login successful!",
          description: `Welcome back, ${result.user.first_name || result.user.email}!`,
        });
        // Don't navigate here - let the useEffect handle navigation based on auth state
      } else {
        // Keep the form data and show error with longer duration
        toast({
          title: "Login failed",
          description: result.error || "Invalid credentials. Please check your email and password.",
          variant: "destructive",
          duration: 5000, // Show error for 5 seconds
        });
      }
    } catch (error) {
      // Keep the form data and show error with longer duration
      toast({
        title: "Login failed",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
        duration: 5000, // Show error for 5 seconds
      });
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: CheckCircle,
      title: "Access Your Dashboard",
      description: "Monitor your chatbot's performance and analytics"
    },
    {
      icon: Shield,
      title: "Secure Account",
      description: "Your data is protected with enterprise-grade security"
    },
    {
      icon: Users,
      title: "Expert Support",
      description: "Get help from our AI specialists anytime"
    },
    {
      icon: Zap,
      title: "Instant Setup",
      description: "Quick access to all BrixBot features"
    }
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-950 overflow-hidden">
      <SEOHead 
        title="Login - BrixBot AI Customer Service Platform" 
        description="Sign in to your BrixBot account to access your AI chatbot dashboard, manage customer service automation, and view analytics."
        isIndexable={true}
      />
      {/* Background Elements */}
      <div className="fixed inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-violet-950/30"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        ></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-20"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-violet-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-violet-500/10 rounded-full blur-2xl animate-pulse delay-2000"></div>
        <div className="absolute bottom-1/4 right-1/4 w-36 h-36 bg-purple-500/10 rounded-full blur-2xl animate-pulse delay-3000"></div>
      </div>

      <div className="relative z-10 py-8 min-h-screen p-4">
        <div className="max-w-6xl w-full mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-violet-600/20 text-violet-300 border-violet-500/30 mb-6">
              <Sparkles className="w-4 h-4 mr-2" />
              Welcome Back
            </Badge>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Welcome Back to
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-purple-400">
                BrixBot
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Sign in to your account to access your AI chatbot dashboard and manage your customer service automation.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Login Form */}
            <Card className="bg-gray-900/50 border-violet-500/20 backdrop-blur-sm relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/5 to-purple-500/5"></div>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-purple-500"></div>
              
              <CardHeader className="text-center relative z-10">
                <div className="w-20 h-20 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <LogIn className="w-10 h-10 text-white" />
                </div>
                <CardTitle className="text-3xl text-white">Sign In</CardTitle>
                <CardDescription className="text-gray-300 text-lg">
                  Enter your credentials to access your dashboard
                </CardDescription>
              </CardHeader>
              
              <CardContent className="relative z-10">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-300 font-medium">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 h-12 rounded-xl"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-gray-300 font-medium">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                        className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500 h-12 rounded-xl pr-12"
                        required
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1 h-10 w-10 text-gray-400 hover:text-gray-300"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="remember"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-violet-600 focus:ring-violet-500"
                      />
                      <Label htmlFor="remember" className="text-gray-300 text-sm">
                        Remember me
                      </Label>
                    </div>
                    <Link to="/forgot-password" className="text-sm text-violet-400 hover:text-violet-300 transition-colors">
                      Forgot password?
                    </Link>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white py-6 text-lg rounded-xl shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 transform hover:scale-105"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                        Signing In...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-3" />
                        Sign In
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-8 text-center">
                  <p className="text-gray-400 text-lg">
                    Don't have an account?{' '}
                    <Link to="/register" className="text-violet-400 hover:text-violet-300 transition-colors font-medium">
                      Sign up
                    </Link>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Benefits Section */}
            <div className="space-y-8">
              <div className="text-center lg:text-left">
                <h2 className="text-3xl font-bold text-white mb-6">
                  Why Choose BrixBot?
                </h2>
                <p className="text-xl text-gray-300 leading-relaxed">
                  Join thousands of businesses already using BrixBot to revolutionize their customer service.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="bg-gray-900/30 border-violet-500/20 backdrop-blur-sm hover:border-violet-400/40 transition-all duration-300 group">
                    <CardContent className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <benefit.icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
                          <p className="text-gray-400 text-sm leading-relaxed">{benefit.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="bg-gradient-to-r from-violet-900/30 to-purple-900/30 rounded-2xl p-8 border border-violet-500/20">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Ready to get started?
                </h3>
                <p className="text-gray-300 mb-6">
                  Sign up now and get your AI chatbot running in just 3-5 business days with our expert setup service.
                </p>
                <Link to="/register">
                  <Button className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white px-8 py-4 rounded-xl shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Create Account
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;